import { formatDate } from "../shared/format";
import type { DueTone } from "../shared/dto";

/**
 * Calendar-date helpers for "next date". Everything here works on ISO dates
 * (YYYY-MM-DD) and never on instants, so a date means the same day to the
 * person who typed it and to the person reading it.
 */

/** The team works in one time zone; "today" is today there, not in UTC —
 *  otherwise "Tomorrow" would turn into "Today" at 21:00 São Paulo time. */
export const TEAM_TIME_ZONE = "America/Sao_Paulo";

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86_400_000;

/** Today's calendar date, YYYY-MM-DD, in the given time zone. */
export function todayIso(now = new Date(), timeZone = TEAM_TIME_ZONE): string {
  // en-CA formats as YYYY-MM-DD; the time zone shifts the calendar day.
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}

/** Midnight UTC of an ISO date, or null when the string is not a real date
 *  (31 Feb, 13th month, wrong shape). */
function toUtc(iso: string): number | null {
  const m = ISO_DATE.exec(iso);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const t = Date.UTC(y, mo - 1, d);
  const back = new Date(t);
  return back.getUTCFullYear() === y && back.getUTCMonth() === mo - 1 && back.getUTCDate() === d ? t : null;
}

/** True for a real calendar date in YYYY-MM-DD form. */
export function isIsoDate(value: string): boolean {
  return toUtc(value) !== null;
}

/** "Sept 21, 2026" — the house date format, year always shown. Anything that
 *  is not a real calendar date is returned as it came. */
export function formatIsoDate(iso: string): string {
  return toUtc(iso) === null ? iso : formatDate(iso);
}

/**
 * How a next date reads relative to today: overdue in red, today in amber,
 * within the week as a plain date, further out muted. Null is "No date".
 */
export function describeNextDate(next: string | null, today: string): { label: string; tone: DueTone } {
  if (!next) return { label: "No date", tone: "neutral" };
  const n = toUtc(next);
  const t = toUtc(today);
  if (n === null || t === null) return { label: next, tone: "neutral" };

  const days = Math.round((n - t) / DAY_MS);
  if (days < 0) return { label: formatIsoDate(next), tone: "overdue" };
  if (days === 0) return { label: "Today", tone: "today" };
  if (days === 1) return { label: "Tomorrow", tone: "soon" };
  return { label: formatIsoDate(next), tone: days < 7 ? "soon" : "neutral" };
}
